/**
 * BIA View Dialog — Displays a read-only view of a BIA process assessment.
 * Extracted from OperationalBIAPage.js for maintainability.
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { AlertTriangle, Truck, Building2, FileCheck, Database } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { extractErrorMessage } from "../utils/acceptSuggestions";

const NEXT_LINE = {
  no_process_manager: "No one can give the first approval yet: no active Process Manager is linked to this process.",
  no_it_approver: "No one can give the first approval yet: there is no active IT Function Manager or IT-DRP Manager in this organization.",
  no_final_approver: "No one can give the final approval yet: this department has no active Department Head other than the person who gave the first approval.",
};

function nextApprovalLine(na) {
  if (!na) return null;
  if (na.step === "submit") return "Next step: submit for review (by someone who can edit this BIA).";
  if (na.step === "none") return null; // L7/L8 handled by status
  if (na.blocked_reason) return NEXT_LINE[na.blocked_reason];
  const names = (na.approver_names || []).join(", ");
  if (na.step === "first") return `First approval: ${names}`;
  if (na.step === "final") return `Final approval: ${names}`;
  return null;
}

export default function BIAViewDialog({ open, onOpenChange, process, getRTOBandColor, api, onRefresh }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [sendBackOpen, setSendBackOpen] = useState(false);
  const [sendBackReason, setSendBackReason] = useState("");
  const [finalOpen, setFinalOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  if (!process) return null;

  const status = (process.status || "draft").toLowerCase();
  const statusLabel = status.replace(/_/g, " ");
  const na = process.next_approval;

  const doStatus = async (target, reason) => {
    if (!api) return;
    setBusy(true);
    try {
      let url = `/bia-engine/processes/${process.id}/status?status=${target}`;
      if (reason) url += `&reason=${encodeURIComponent(reason)}`;
      await api.put(url);
      toast.success(`Status updated to ${target.replace(/_/g, " ")}`);
      setSendBackOpen(false); setFinalOpen(false); setArchiveOpen(false); setSendBackReason("");
      if (onRefresh) await onRefresh(process);
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to update status"));
    } finally {
      setBusy(false);
    }
  };
  const sendBackTarget = status === "pending_review" ? "draft" : status === "first_approved" ? "pending_review" : "draft";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{process.process_name}</DialogTitle>
          <DialogDescription>{process.department}</DialogDescription>
        </DialogHeader>

        {/* BP-A3 — status, who-approves-next line, and approval actions */}
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3" data-testid="bia-approval-bar">
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500">Status</span>
            <Badge variant="outline" className="capitalize" data-testid="bia-status-badge">{statusLabel}</Badge>
          </div>
          {status === "approved" ? (
            <p className="text-sm text-slate-600" data-testid="next-approval-line">Approved.</p>
          ) : status === "archived" ? (
            <p className="text-sm text-slate-600" data-testid="next-approval-line">Archived.</p>
          ) : nextApprovalLine(na) ? (
            <p className="text-sm text-slate-600" data-testid="next-approval-line">{nextApprovalLine(na)}</p>
          ) : null}
          <div className="flex flex-wrap gap-2">
            {process.can_submit && (
              <Button size="sm" disabled={busy} onClick={() => doStatus("pending_review")} data-testid="btn-submit-review">Submit for review</Button>
            )}
            {process.can_approve && (
              <Button size="sm" disabled={busy} onClick={() => doStatus("first_approved")} data-testid="btn-approve">Approve</Button>
            )}
            {process.can_final && (
              <Button size="sm" disabled={busy} onClick={() => setFinalOpen(true)} data-testid="btn-final-approve">Final approve</Button>
            )}
            {process.can_send_back && (
              <Button size="sm" variant="outline" disabled={busy} onClick={() => setSendBackOpen(true)} data-testid="btn-send-back">Send back</Button>
            )}
            {process.can_archive && status !== "archived" && (
              <Button size="sm" variant="outline" disabled={busy} onClick={() => setArchiveOpen(true)} data-testid="btn-archive">Archive</Button>
            )}
            {process.can_archive && status === "archived" && (
              <Button size="sm" variant="outline" disabled={busy} onClick={() => doStatus("draft")} data-testid="btn-restore">Restore</Button>
            )}
          </div>
        </div>

        <div className="space-y-6">
          {/* RTO/RPO Summary */}
          <div className="grid md:grid-cols-3 gap-4">
            <Card className={getRTOBandColor(process.calculated_rto_band)}>
              <CardContent className="pt-4 text-center">
                <p className="text-sm opacity-75">RTO</p>
                <p className="text-3xl font-bold">{process.calculated_rto_hours}h</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 text-center">
                <p className="text-sm text-slate-500">RPO</p>
                <p className="text-3xl font-bold text-slate-900">{process.calculated_rpo_hours}h</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 text-center">
                <p className="text-sm text-slate-500">Staff Recovery</p>
                <p className="text-3xl font-bold text-slate-900">{process.staff_recovery_time_hours}h</p>
              </CardContent>
            </Card>
          </div>

          {/* RTO Explanation */}
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800">
              <strong>RTO Calculation:</strong> {process.rto_explanation}
            </p>
          </div>

          {/* Per-Asset RPO Breakdown (D2) */}
          {(process.asset_data_sensitivity || []).some(s => s.holds_sensitive_data && s.asset_rpo_hours != null) && (
            <Card data-testid="view-rpo-breakdown">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2"><Database className="w-4 h-4 text-teal-700" /> Per-Asset RPO Breakdown</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1.5">
                {(process.asset_data_sensitivity || []).filter(s => s.holds_sensitive_data && s.asset_rpo_hours != null).map((s, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm border-b border-slate-100 py-1.5" data-testid={`view-rpo-asset-${idx}`}>
                    <span className="text-slate-700 font-medium">{s.asset_name || s.asset_ref}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">{s.protection_hint || "—"}</span>
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">{s.asset_rpo_hours}h</Badge>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Gaps */}
          {(process.dependency_gaps?.length > 0 || process.resource_gaps?.length > 0) && (
            <Card className="border-amber-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-amber-800 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" /> Identified Gaps
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {process.dependency_gaps?.map((gap, idx) => (
                  <div key={idx} className="p-3 bg-amber-50 rounded border border-amber-200">
                    <Badge variant={gap.severity === "high" ? "destructive" : "warning"} className="mb-1">
                      {gap.gap_type.replace("_", " ")}
                    </Badge>
                    <p className="text-sm">{gap.message}</p>
                  </div>
                ))}
                {process.resource_gaps?.map((gap, idx) => (
                  <div key={idx} className="p-3 bg-amber-50 rounded border border-amber-200">
                    <Badge variant={gap.severity === "high" ? "destructive" : "warning"} className="mb-1">
                      {gap.gap_type}
                    </Badge>
                    <p className="text-sm">{gap.message}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Dependencies */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Dependencies</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500 mb-2">Upstream</p>
                  {process.upstream_processes?.length === 0 && process.custom_upstream_dependencies?.length === 0 ? (
                    <p className="text-sm text-slate-400">None</p>
                  ) : (
                    <div className="space-y-1">
                      {process.upstream_processes?.map((proc) => (
                        <div key={proc.id} className="p-2 bg-slate-50 rounded text-sm">
                          {proc.process_name} <span className="text-slate-500">({proc.calculated_rto_hours}h RTO)</span>
                        </div>
                      ))}
                      {process.custom_upstream_dependencies?.map((dep, idx) => (
                        <div key={`custom-up-${idx}`} className="p-2 bg-blue-50 rounded text-sm">
                          {dep} <Badge variant="outline" className="ml-2 text-xs">External</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-2">Downstream</p>
                  {process.downstream_processes?.length === 0 && process.custom_downstream_dependencies?.length === 0 ? (
                    <p className="text-sm text-slate-400">None</p>
                  ) : (
                    <div className="space-y-1">
                      {process.downstream_processes?.map((proc) => (
                        <div key={proc.id} className="p-2 bg-slate-50 rounded text-sm">
                          {proc.process_name} <span className="text-slate-500">({proc.calculated_rto_hours}h RTO)</span>
                        </div>
                      ))}
                      {process.custom_downstream_dependencies?.map((dep, idx) => (
                        <div key={`custom-down-${idx}`} className="p-2 bg-purple-50 rounded text-sm">
                          {dep} <Badge variant="outline" className="ml-2 text-xs">External</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Vendors with RTO Inheritance */}
          {process.vendor_dependencies?.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Truck className="w-4 h-4 text-slate-400" /> Vendors (RTO Inherited)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {process.vendor_dependencies.map((vendor, idx) => (
                    <div key={idx} className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{vendor.vendor_name}</p>
                          <p className="text-xs text-slate-500">
                            {vendor.contact_email} {vendor.contact_phone && `\u2022 ${vendor.contact_phone}`}
                          </p>
                        </div>
                        <div className="text-right">
                          <Badge className="bg-purple-100 text-purple-700">{vendor.criticality}</Badge>
                          <p className="text-xs text-emerald-600 mt-1">
                            RTO: {vendor.effective_rto_hours || vendor.inherited_rto_hours || process.calculated_rto_hours}h
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Facilities with Activation Time */}
          {process.facility_dependencies?.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-slate-400" /> Facilities (RTO + Activation Time)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {process.facility_dependencies.map((facility, idx) => (
                    <div key={idx} className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{facility.facility_name}</p>
                          <p className="text-xs text-slate-500">
                            {facility.city} &bull; {facility.capacity_percentage}% capacity
                            {facility.is_remote_capable && " \u2022 Remote OK"}
                          </p>
                        </div>
                        <div className="text-right">
                          <Badge variant="outline" className="capitalize">{facility.facility_type}</Badge>
                          <p className="text-xs text-emerald-600 mt-1">
                            Effective RTO: {facility.effective_rto_hours || (process.calculated_rto_hours + (facility.activation_time_hours || 0))}h
                            {facility.activation_time_hours > 0 && ` (+${facility.activation_time_hours}h activation)`}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Create Recovery Plan Button */}
          <div className="pt-4 border-t">
            <Button
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              onClick={() => navigate(`/recovery-wizard/bia/${process.id}`)}
              data-testid="create-recovery-plan-btn"
            >
              <FileCheck className="w-4 h-4 mr-2" /> Create Recovery Plan
            </Button>
            <p className="text-xs text-slate-500 mt-2 text-center">
              ISO 22301 Clause 8.3-8.4: Define recovery strategy and procedures for this process
            </p>
          </div>
        </div>
      </DialogContent>

      {/* Send back for changes */}
      <Dialog open={sendBackOpen} onOpenChange={(o) => !o && setSendBackOpen(false)}>
        <DialogContent data-testid="send-back-dialog">
          <DialogHeader><DialogTitle>Send back for changes</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2">
            <label className="text-sm font-medium text-slate-700">Reason</label>
            <Textarea
              value={sendBackReason}
              maxLength={500}
              onChange={(e) => setSendBackReason(e.target.value.slice(0, 500))}
              placeholder="Explain what needs to change"
              data-testid="send-back-reason"
            />
            <p className="text-xs text-slate-400 text-right">{sendBackReason.length}/500</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendBackOpen(false)} data-testid="send-back-cancel">Cancel</Button>
            <Button disabled={!sendBackReason.trim() || busy} onClick={() => doStatus(sendBackTarget, sendBackReason.trim())} data-testid="send-back-confirm">Send back</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Final approval confirmation */}
      <Dialog open={finalOpen} onOpenChange={(o) => !o && setFinalOpen(false)}>
        <DialogContent data-testid="final-confirm-dialog">
          <DialogHeader><DialogTitle>Give final approval?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600 py-2">Final approval starts the follow-up tasks for this process.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFinalOpen(false)} data-testid="final-cancel">Cancel</Button>
            <Button disabled={busy} onClick={() => doStatus("approved")} data-testid="final-confirm">Final approve</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive confirmation */}
      <Dialog open={archiveOpen} onOpenChange={(o) => !o && setArchiveOpen(false)}>
        <DialogContent data-testid="archive-confirm-dialog">
          <DialogHeader><DialogTitle>Archive this BIA?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600 py-2">An archived BIA can be restored to draft by its Department Head or a BCM Manager.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setArchiveOpen(false)} data-testid="archive-cancel">Cancel</Button>
            <Button disabled={busy} onClick={() => doStatus("archived")} data-testid="archive-confirm">Archive</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
